import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, Card, CardContent, 
  CircularProgress, Avatar, Chip, Button, Divider, 
  LinearProgress, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert
} from '@mui/material';
import { 
  School, People, Assignment, AssessmentOutlined,
  MenuBook, FileDownload, PlayArrow,
  Wifi, HistoryToggleOff, AdminPanelSettings, TrendingUp, InfoOutlined
} from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, AreaChart, Area } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useSystemSettings } from '../components/DynamicThemeProvider';
import apiClient from '../services/api';
import { exportToExcel } from '../utils/excel';

interface CourseGrade {
  id: number;
  item_name: string;
  item_type: string;
  grade: number | null;
  feedback: string;
  date_graded: string;
}

interface StudentCourse {
  course_id: number;
  short_name: string;
  full_name: string;
  role: string;
  grades: CourseGrade[];
  completionRate: number;
}

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { settings } = useSystemSettings();

  
  // Student specific state
  const [studentCourses, setStudentCourses] = useState<StudentCourse[]>([]);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [expandedCourse, setExpandedCourse] = useState<number | null>(null);

  // Admin specific dynamic state
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const stats = [
    { title: 'Programas Activos', value: dashboardData?.kpis?.programs_count ?? '-', icon: <School />, color: '#7C4DFF', grad: 'linear-gradient(135deg, #7C4DFF 0%, #0052FF 100%)' },
    { title: 'Estudiantes Totales', value: dashboardData?.kpis?.students_count ?? '-', icon: <People />, color: '#00B074', grad: 'linear-gradient(135deg, #00B074 0%, #00D9A5 100%)' },
    { title: 'Cursos Moodle', value: dashboardData?.kpis?.moodle_courses_count ?? '-', icon: <Assignment />, color: '#0070E0', grad: 'linear-gradient(135deg, #0070E0 0%, #1A8CFF 100%)' },
    { title: 'Promedio General', value: dashboardData?.kpis?.overall_average ?? '-', icon: <AssessmentOutlined />, color: '#FF5252', grad: 'linear-gradient(135deg, #FF5252 0%, #FF9F43 100%)' },
  ];

  useEffect(() => {
    const loadDashboardStats = async () => {
      if (user?.role === 'student') return;
      setLoadingDashboard(true);
      try {
        const response = await apiClient.get('/reports/reports/dashboard_stats/');
        if (response.data && response.data.success) {
          setDashboardData(response.data.data);
        }
      } catch (err) {
        console.error('Failed to load dashboard dynamic stats:', err);
      } finally {
        setLoadingDashboard(false);
      }
    };
    loadDashboardStats();
  }, [user]);

  useEffect(() => {
    const loadStudentData = async () => {
      if (user?.role !== 'student') return;
      setLoadingStudent(true);
      
      try {
        // 1. Fetch student enrollments
        const enrollmentsRes = await apiClient.get(`/moodle/enrollments/?user=${user.id}`);
        const coursesList: StudentCourse[] = [];
        
        if (enrollmentsRes.data && enrollmentsRes.data.results) {
          for (const enrol of enrollmentsRes.data.results) {
            const course = enrol.course;
            
            // 2. Fetch grades for each course
            const gradesRes = await apiClient.get(`/moodle/grades/?enrollment__user=${user.id}&enrollment__course=${course.id}`);
            const gradesList: CourseGrade[] = gradesRes.data?.results || [];
            
            // Calculate a mock/simulated activity completion rate
            const gradedItems = gradesList.filter(g => g.grade !== null).length;
            const totalItems = gradesList.length || 1;
            const completionRate = Math.round((gradedItems / totalItems) * 100);
            
            coursesList.push({
              course_id: course.id,
              short_name: course.short_name,
              full_name: course.full_name,
              role: enrol.role === 'student' ? 'Estudiante' : 'Docente',
              grades: gradesList,
              completionRate: completionRate || 0
            });
          }
        }
        
        setStudentCourses(coursesList);
        if (coursesList.length > 0) {
          setExpandedCourse(coursesList[0].course_id);
        }
      } catch (err) {
        console.error('Failed to load student dashboard academic portal:', err);
      } finally {
        setLoadingStudent(false);
      }
    };

    loadStudentData();
  }, [user]);

  const handleExportGrades = (course: StudentCourse) => {
    const headers = ['Ítem de Calificación', 'Tipo', 'Nota', 'Min', 'Max', 'Retroalimentación', 'Fecha Calificación'];
    const keys = ['item_name', 'item_type', 'grade', 'grade_min', 'grade_max', 'feedback', 'date_graded'];
    exportToExcel(course.grades, headers, keys, `Boletin_Notas_${course.short_name}`);
  };

  const getGradeColor = (grade: number | null) => {
    if (grade === null) return '#A6B3C0';
    if (grade >= 90) return '#00D9A5'; // Excellent
    if (grade >= 70) return '#0070E0'; // Good
    if (grade >= 50) return '#FF9F43'; // Regular
    return '#FF5252'; // Needs improvement
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '85vh', pb: 4 }}>
      {/* Subtle Breathing Nebula Glow behind content */}
      <Box 
        sx={{
          position: 'absolute',
          top: '-10%',
          left: '20%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,112,224,0.05) 0%, rgba(124,77,255,0.01) 75%)',
          filter: 'blur(50px)',
          zIndex: -1,
          animation: 'breathing-nebula 9s ease-in-out infinite'
        }}
      />

      {/* Main welcome banner */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: { xs: 3, md: 4 }, 
          mb: 4, 
          borderRadius: 4,
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(255, 255, 255, 0.75)',
          borderColor: 'rgba(226, 232, 240, 0.8)',
          boxShadow: '0 8px 32px rgba(10, 37, 64, 0.02)'
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={9}>
            <Typography variant="h4" fontWeight={800} sx={{ color: '#0A2540', display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              ¡Bienvenido, {user?.first_name}!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem', maxHeight: 100 }}>
              {user?.role === 'student' 
                ? 'Accede al reporte de tus calificaciones históricas y avance de actividades sincronizadas desde Moodle.' 
                : `${settings.institution_name} - Panel General de Control y Monitoreo de Datos.`
              }
            </Typography>
            <Box display="flex" gap={1} mt={2.5} flexWrap="wrap">
              <Chip 
                label={user?.role === 'admin' ? 'Administrador' : user?.role === 'coordinator' ? 'Coordinador Académico' : user?.role === 'teacher' ? 'Docente' : 'Estudiante'} 
                icon={<AdminPanelSettings sx={{ color: 'inherit !important' }} />}
                sx={{ 
                  bgcolor: user?.role === 'admin' ? '#7C4DFF20' : '#0070E020', 
                  color: user?.role === 'admin' ? '#7C4DFF' : '#0070E0', 
                  fontWeight: 700,
                  fontSize: '0.85rem'
                }} 
              />
              <Chip 
                label={`SSO Moodle: Habilitado`} 
                icon={<Wifi sx={{ color: '#00B074 !important' }} />}
                sx={{ bgcolor: '#00B07415', color: '#00B074', fontWeight: 600, fontSize: '0.85rem' }} 
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            <Avatar 
              src={settings.logo || undefined}
              variant="rounded"
              sx={{ 
                width: 90, 
                height: 90, 
                bgcolor: '#FFFFFF', 
                border: '1px solid #E2E8F0', 
                objectFit: 'contain',
                p: 0.5,
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
              }}
            >
              <School color="primary" sx={{ fontSize: 50 }} />
            </Avatar>
          </Grid>
        </Grid>
      </Paper>

      {/* STUDENT PORTAL LAYOUT */}
      {user?.role === 'student' ? (
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#0A2540', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MenuBook color="secondary" />
            Mis Cursos y Calificaciones Históricas
          </Typography>

          {loadingStudent ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress color="secondary" />
            </Box>
          ) : studentCourses.length === 0 ? (
            <Card variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No tienes asignaturas registradas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Si acabas de ingresar por primera vez, espera a que el Administrador ejecute la sincronización académica general de Moodle.
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {/* Left Side: Courses List */}
              <Grid item xs={12} md={4}>
                <Box display="flex" flexDirection="column" gap={2}>
                  {studentCourses.map(course => (
                    <Card 
                      key={course.course_id}
                      variant="outlined"
                      onClick={() => setExpandedCourse(course.course_id)}
                      sx={{ 
                        cursor: 'pointer',
                        borderColor: expandedCourse === course.course_id ? 'secondary.main' : 'rgba(226, 232, 240, 0.8)',
                        borderWidth: expandedCourse === course.course_id ? '2px' : '1px',
                        transition: 'all 0.2s',
                        bgcolor: expandedCourse === course.course_id ? 'rgba(0, 112, 224, 0.02)' : '#FFFFFF',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(10, 37, 64, 0.05)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 700, textTransform: 'uppercase', tracking: 1 }}>
                          {course.short_name}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0A2540', mt: 0.5, lineHeight: 1.2 }}>
                          {course.full_name}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={3.5}>
                          <Chip label={course.role} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={700} color="text.secondary">
                              {course.completionRate}%
                            </Typography>
                            <Box sx={{ width: 60 }}>
                              <LinearProgress variant="determinate" value={course.completionRate} color="secondary" sx={{ height: 6, borderRadius: 3 }} />
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Grid>

              {/* Right Side: Selected Course Grades */}
              <Grid item xs={12} md={8}>
                {expandedCourse && studentCourses.find(c => c.course_id === expandedCourse) && (() => {
                  const course = studentCourses.find(c => c.course_id === expandedCourse)!;
                  return (
                    <Card variant="outlined" sx={{ borderRadius: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.8)' }}>
                      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={2} mb={3}>
                          <Box>
                            <Typography variant="caption" color="secondary" fontWeight={700}>
                              BOLETÍN ACADÉMICO DETALLADO
                            </Typography>
                            <Typography variant="h5" fontWeight={800} sx={{ color: '#0A2540', mt: 0.5 }}>
                              {course.full_name}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleExportGrades(course)}
                            startIcon={<FileDownload />}
                            sx={{ borderRadius: 30, px: 3, textTransform: 'none', fontWeight: 600 }}
                          >
                            Descargar Boletín Excel
                          </Button>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {course.grades.length === 0 ? (
                          <Box textAlign="center" py={6}>
                            <HistoryToggleOff sx={{ fontSize: 50, color: 'text.disabled', mb: 1.5 }} />
                            <Typography variant="body1" color="text.secondary">
                              No hay ítems de calificaciones registrados para esta asignatura en CreaSync.
                            </Typography>
                          </Box>
                        ) : (
                          <Box display="flex" flexDirection="column" gap={2.5}>
                            {course.grades.map(grade => (
                              <Card key={grade.id} variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#FFFFFF', transition: 'none', '&:hover': { transform: 'none', boxShadow: 'none' } }}>
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={12} sm={8}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0A2540' }}>
                                      {grade.item_name}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                      <Chip label={grade.item_type} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                                      {grade.date_graded && (
                                        <Typography variant="caption" color="text.secondary">
                                          Calificado el: {new Date(grade.date_graded).toLocaleDateString()}
                                        </Typography>
                                      )}
                                    </Box>
                                    {(() => {
                                      const clean = (grade.feedback || '')
                                        .replace(/&nbsp;/g, ' ')
                                        .replace(/<[^>]*>/g, '')
                                        .trim();
                                      return clean ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, borderLeft: '3px solid #CBD5E1', fontSize: '0.85rem' }}>
                                          <strong>Retroalimentación:</strong> {clean}
                                        </Typography>
                                      ) : null;
                                    })()}
                                  </Grid>
                                  <Grid item xs={12} sm={4} display="flex" flexDirection="column" alignItems={{ xs: 'flex-start', sm: 'flex-end' }} justifyContent="center">
                                    <Typography variant="caption" color="text.secondary">
                                      Nota Obtenida
                                    </Typography>
                                    <Typography 
                                      variant="h4" 
                                      fontWeight={900} 
                                      sx={{ 
                                        color: getGradeColor(grade.grade), 
                                        lineHeight: 1.1,
                                        mt: 0.5,
                                        display: 'flex',
                                        alignItems: 'baseline'
                                      }}
                                    >
                                      {grade.grade !== null ? grade.grade : '-'}
                                      <span style={{ fontSize: '1rem', fontWeight: 600, color: '#94A3B8', marginLeft: 4 }}>
                                        / 100
                                      </span>
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Card>
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}
              </Grid>
            </Grid>
          )}
        </Box>
      ) : (
        /* ADMIN / COORDINATOR PORTAL LAYOUT */
        <Box>
          {/* Main Tabs for Admin Panel */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 4, borderRadius: 4, bgcolor: 'rgba(255, 255, 255, 0.75)' }}>
            <Tabs 
              value={tabValue} 
              onChange={(_, val) => setTabValue(val)} 
              indicatorColor="secondary" 
              textColor="secondary"
            >
              <Tab label="Vista General" icon={<AdminPanelSettings />} iconPosition="start" sx={{ fontWeight: 700, textTransform: 'none' }} />
              <Tab label="Reportes Avanzados" icon={<AssessmentOutlined />} iconPosition="start" sx={{ fontWeight: 700, textTransform: 'none' }} />
            </Tabs>
          </Paper>

          {loadingDashboard ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <Box>
              {/* TAB 0: GENERAL OVERVIEW */}
              {tabValue === 0 && (
                <Box>
                  <Grid container spacing={3} mb={4}>
                    {stats.map((stat) => (
                      <Grid item xs={12} sm={6} md={3} key={stat.title}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            position: 'relative',
                            overflow: 'hidden',
                            backdropFilter: 'blur(8px)', 
                            bgcolor: 'rgba(255, 255, 255, 0.75)',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 30px rgba(10, 37, 64, 0.08)',
                              borderColor: stat.color
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box display="flex" justifyContent="space-between" mb={2}>
                              <Box
                                sx={{
                                  background: stat.grad,
                                  color: '#FFFFFF',
                                  p: 1.5,
                                  borderRadius: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                                }}
                              >
                                {stat.icon}
                              </Box>
                            </Box>
                            <Typography variant="h3" fontWeight={850} sx={{ color: '#0A2540', mb: 0.5 }}>
                              {stat.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                              {stat.title}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  <Grid container spacing={3}>
                    {/* Recent activity timeline */}
                    <Grid item xs={12} md={7}>
                      <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.75)' }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#0A2540', mb: 3 }}>
                          Actividad Reciente de Sincronización
                        </Typography>
                        
                        {dashboardData?.recent_sync_logs?.length > 0 ? (
                          <Box display="flex" flexDirection="column" gap={3} pl={1} sx={{ borderLeft: '2px solid #E2E8F0', position: 'relative' }}>
                            {dashboardData.recent_sync_logs.map((log: any) => (
                              <Box key={log.id} sx={{ position: 'relative' }}>
                                <Box 
                                  sx={{ 
                                    position: 'absolute', 
                                    left: -14, 
                                    top: 4, 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    bgcolor: log.status === 'success' ? '#00B074' : log.status === 'error' ? '#FF5252' : '#FF9F43', 
                                    boxShadow: `0 0 0 4px ${log.status === 'success' ? '#00B07420' : '#FF525220'}` 
                                  }} 
                                />
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0A2540', textTransform: 'capitalize' }}>
                                  Sincronización de {log.sync_type} finalizada ({log.status})
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(log.created_at).toLocaleString()} • Tarea del Sistema
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Se procesaron {log.records_processed} registros locales. 
                                  Creados: {log.records_created} • Actualizados: {log.records_updated}.
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box pl={1} sx={{ borderLeft: '2px solid #E2E8F0' }}>
                            <Typography variant="body2" color="text.secondary">
                              No hay bitácoras de sincronización registradas.
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* Quick action buttons & status */}
                    <Grid item xs={12} md={5}>
                      <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.75)', mb: 3 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#0A2540', mb: 2 }}>
                          Estado de Integración
                        </Typography>
                        
                        <Box display="flex" alignItems="center" gap={2} p={2} sx={{ bgcolor: '#00B07410', borderRadius: 3, border: '1px solid rgba(0, 176, 116, 0.15)', mb: 2.5 }}>
                          <Wifi sx={{ color: '#00B074', fontSize: 30 }} />
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                              Conexión Activa con Moodle
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Servicio Web respondiendo correctamente
                            </Typography>
                          </Box>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={1.5}>
                          <Button
                            variant="contained"
                            color="secondary"
                            href="/moodle-sync"
                            startIcon={<PlayArrow />}
                            fullWidth
                            sx={{ borderRadius: 30, py: 1.2, fontWeight: 600, boxShadow: 'none' }}
                          >
                            Disparar Sincronización Manual
                          </Button>
                          <Button
                            variant="outlined"
                            href="/students"
                            fullWidth
                            sx={{ borderRadius: 30, py: 1.2, fontWeight: 600, borderColor: '#E2E8F0', color: 'text.primary' }}
                          >
                            Ver Directorio de Estudiantes
                          </Button>
                          <Button
                            variant="outlined"
                            href="/courses"
                            fullWidth
                            sx={{ borderRadius: 30, py: 1.2, fontWeight: 600, color: 'secondary.main', borderColor: 'secondary.main' }}
                          >
                            Ver Directorio de Cursos
                          </Button>
                          {user?.role === 'admin' && (
                            <Button
                              variant="outlined"
                              color="secondary"
                              href="/audit-logs"
                              fullWidth
                              startIcon={<HistoryToggleOff />}
                              sx={{ 
                                borderRadius: 30, 
                                py: 1.2, 
                                fontWeight: 600, 
                                borderStyle: 'dashed',
                                borderColor: 'secondary.main',
                                '&:hover': {
                                  borderStyle: 'dashed',
                                }
                              }}
                            >
                              Bitácora de Auditoría
                            </Button>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* TAB 1: ADVANCED REPORTS */}
              {tabValue === 1 && (
                <Box>
                  <Grid container spacing={3} mb={4}>
                    {/* Grade General Distribution Chart */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 4 }}>
                        <Typography variant="subtitle1" fontWeight={750} sx={{ color: '#0A2540', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUp color="secondary" />
                          Distribución General de Calificaciones Moodle
                        </Typography>
                        
                        {dashboardData?.grade_distribution ? (
                          <Box sx={{ width: '100%', height: 240 }}>
                            <ResponsiveContainer>
                              <AreaChart
                                data={[
                                  { name: 'Reprobado (<50)', alumnos: dashboardData.grade_distribution.insufficient },
                                  { name: 'Regular (50-69)', alumnos: dashboardData.grade_distribution.regular },
                                  { name: 'Bueno (70-89)', alumnos: dashboardData.grade_distribution.good },
                                  { name: 'Excelente (90-100)', alumnos: dashboardData.grade_distribution.excellent }
                                ]}
                                margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                              >
                                <XAxis dataKey="name" fontSize={10} stroke="#94A3B8" />
                                <YAxis fontSize={10} stroke="#94A3B8" allowDecimals={false} />
                                <Tooltip 
                                  contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                                />
                                <defs>
                                  <linearGradient id="colorAlumnos" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#0070E0" stopOpacity={0.4}/>
                                    <stop offset="95%" stopColor="#0070E0" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <Area type="monotone" dataKey="alumnos" stroke="#0070E0" strokeWidth={2} fillOpacity={1} fill="url(#colorAlumnos)" />
                              </AreaChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Box display="flex" justifyContent="center" alignItems="center" height={240}>
                            <CircularProgress color="secondary" />
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* Enrolled Students per Course Moodle */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 4 }}>
                        <Typography variant="subtitle1" fontWeight={750} sx={{ color: '#0A2540', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <School color="secondary" />
                          Alumnado Inscrito por Curso Moodle
                        </Typography>
                        
                        {dashboardData?.courses ? (
                          <Box sx={{ width: '100%', height: 240 }}>
                            <ResponsiveContainer>
                              <BarChart
                                data={dashboardData.courses.slice(0, 5)}
                                margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                              >
                                <XAxis dataKey="short_name" fontSize={10} stroke="#94A3B8" />
                                <YAxis fontSize={10} stroke="#94A3B8" allowDecimals={false} />
                                <Tooltip 
                                  contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                                />
                                <Bar dataKey="enrollments_count" name="Estudiantes" fill="#00D9A5" radius={[4, 4, 0, 0]} maxBarSize={40} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Box display="flex" justifyContent="center" alignItems="center" height={240}>
                            <CircularProgress color="secondary" />
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Early Warning Risk alert directory */}
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ borderRadius: 4 }}>
                        <CardContent sx={{ p: 3.5 }}>
                          <Typography variant="h6" fontWeight={800} sx={{ color: '#0A2540', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoOutlined color="error" />
                            Alerta Temprana - Alumnos en Riesgo Académico (Promedio &lt; 50)
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Los siguientes alumnos registran un promedio de notas inferior al umbral mínimo de aprobación general (50/100).
                          </Typography>

                          {!dashboardData || !dashboardData.at_risk_students ? (
                            <Box display="flex" justifyContent="center" py={4}>
                              <CircularProgress size={30} color="secondary" />
                            </Box>
                          ) : dashboardData.at_risk_students.length === 0 ? (
                            <Alert severity="success" sx={{ borderRadius: 2 }}>
                              <strong>¡Excelente desempeño!</strong> No hay alumnos en riesgo académico reportados en la plataforma.
                            </Alert>
                          ) : (
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                              <Table size="small">
                                <TableHead sx={{ bgcolor: 'rgba(254, 242, 242, 0.6)' }}>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Nombre Estudiante</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Correo Electrónico</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }} width={160}>Promedio General</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }} width={140}>Estado Alerta</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dashboardData.at_risk_students.map((student: any) => (
                                    <TableRow key={student.id} hover>
                                      <TableCell>
                                        <Typography fontWeight={750} variant="body2" sx={{ color: '#0A2540' }}>
                                          {student.name}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>{student.email}</TableCell>
                                      <TableCell align="right">
                                        <Typography fontWeight={850} color="error.main">
                                          {student.average.toFixed(1)} / 100
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Chip label="Riesgo Alto" color="error" size="small" sx={{ fontWeight: 700, height: 20 }} />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DashboardPage;
